import axios from 'axios';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { logger } from '../utils/logger';

// Extend axios config to include timing
interface TimedAxiosRequestConfig extends InternalAxiosRequestConfig {
  metadata?: {
    startTime: number;
  };
}

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Request interceptor
api.interceptors.request.use(
  (config: TimedAxiosRequestConfig) => {
    // Add timing metadata
    config.metadata = { startTime: Date.now() };
    
    // Log outgoing request
    logger.api(
      config.method?.toUpperCase() || 'GET',
      config.url || '',
      undefined,
      undefined,
      {
        params: config.params,
        hasBody: !!config.data,
      }
    );
    
    // Add auth token if available
    const token = localStorage.getItem('fleet_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    logger.error('Request interceptor error', error);
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    const config = response.config as TimedAxiosRequestConfig;
    const duration = config.metadata?.startTime 
      ? Date.now() - config.metadata.startTime 
      : undefined;
    
    // Log successful response
    logger.api(
      config.method?.toUpperCase() || 'GET',
      config.url || '',
      response.status,
      duration,
      {
        dataSize: JSON.stringify(response.data).length,
      }
    );
    
    return response;
  },
  (error: AxiosError) => {
    const config = error.config as TimedAxiosRequestConfig | undefined;
    const duration = config?.metadata?.startTime 
      ? Date.now() - config.metadata.startTime 
      : undefined;
    
    // Log error response
    const status = error.response?.status;
    const errorData = error.response?.data as { error?: string; message?: string } | undefined;
    const errorMessage = errorData?.error || errorData?.message || error.message;
    
    logger.error(
      `API Error: ${errorMessage}`,
      error,
      {
        method: config?.method?.toUpperCase(),
        url: config?.url,
        status,
        duration,
      }
    );

    // Handle specific error codes
    if (status === 401) {
      logger.warn('Unauthorized - session may have expired');
      // Clear token and redirect to login
      localStorage.removeItem('fleet_token');
      localStorage.removeItem('fleet_user');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    } else if (status === 403) {
      logger.warn('Forbidden - insufficient permissions');
      window.dispatchEvent(new Event('fleet:forbidden')); // AuthContext: refresh permissions
      // Banner only for user actions, not background GET polls.
      if (config?.method?.toLowerCase() !== 'get') window.dispatchEvent(new Event('fleet:forbidden-action'));
    } else if (status === 404) {
      logger.warn(`Resource not found: ${config?.url}`);
    } else if (status === 500) {
      logger.error('Server error - please try again later');
    } else if (!error.response) {
      // Network error
      logger.error('Network error - check your connection', error);
    }

    // The driver pressed "Complete all tasks" on a list that is still open, and
    // core may cancel what we add. The server warns, the officer decides: yes
    // re-sends with the acknowledgement, which the server logs against the user.
    const body = error.response?.data as { code?: string; error?: string } | undefined;
    if (body?.code === 'DRIVER_LIST_CLOSING' && config && confirm(`${body.error}\n\nAssign anyway?`)) {
      const data = typeof config.data === 'string' ? JSON.parse(config.data) : (config.data ?? {});
      return api.request({ ...config, data: { ...data, acknowledgeListClosing: true } });
    }

    return Promise.reject(error);
  }
);

export default api;
